export type AppRole = 'guest' | 'customer' | 'vendor' | 'admin' | 'superadmin' | 'unknown';

export interface StoredUser {
  id?: number;
  full_name?: string;
  email?: string;
  role?: string;
  vendor_type?: string;
  store?: {
    vendor_type?: string;
    [key: string]: unknown;
  };
  account_status?: {
    is_active?: boolean;
    is_vendor?: boolean;
    can_access_vendor_portal?: boolean;
    can_sell?: boolean;
    can_use_pos?: boolean;
    restrictions?: string[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface StoredSession {
  token: string | null;
  user: StoredUser | null;
  role: AppRole;
  isAuthenticated: boolean;
}

const ACCESS_TOKEN_KEY = 's360_access';
const REFRESH_TOKEN_KEY = 's360_refresh';
const USER_KEY = 's360_user';

export function normalizeRole(role?: string | null): AppRole {
  if (!role) return 'guest';
  const value = String(role).toLowerCase();
  if (value === 'customer' || value === 'user') return 'customer';
  if (value === 'vendor') return 'vendor';
  if (value === 'admin') return 'admin';
  if (value === 'superadmin') return 'superadmin';
  return 'unknown';
}

export function isAdminRole(role?: string | null): boolean {
  const normalized = normalizeRole(role);
  return normalized === 'admin' || normalized === 'superadmin';
}

export function isVendorRole(role?: string | null): boolean {
  return normalizeRole(role) === 'vendor';
}

function resolveVendorType(user?: StoredUser | null): string {
  if (!user || typeof user !== 'object') return '';
  const direct = typeof user.vendor_type === 'string' ? user.vendor_type : '';
  const fromStore =
    user.store && typeof user.store === 'object' && typeof user.store.vendor_type === 'string'
      ? user.store.vendor_type
      : '';
  return String(fromStore || direct).toLowerCase();
}

export function getVendorWorkspacePath(user?: StoredUser | null): string {
  const vendorType = resolveVendorType(user);
  if (vendorType === 'showroom') return '/showroom';
  return '/vendor';
}

export function getDashboardPathForRole(role?: string | null): string {
  const normalized = normalizeRole(role);
  if (normalized === 'admin' || normalized === 'superadmin') return '/admin';
  if (normalized === 'vendor') return '/myprofil';
  if (normalized === 'customer') return '/customer-dashboard';
  return '/login';
}

export function getStoredAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getStoredRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function readStoredUser(): StoredUser | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as StoredUser;
    return null;
  } catch {
    return null;
  }
}

export function clearStoredSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function notifySessionChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('s360-auth-changed'));
}

export function getGuestSession(): StoredSession {
  return {
    token: null,
    user: null,
    role: 'guest',
    isAuthenticated: false,
  };
}

export function readStoredSession(): StoredSession {
  const token = getStoredAccessToken();
  const user = readStoredUser();
  return {
    token,
    user,
    role: normalizeRole(user?.role),
    isAuthenticated: Boolean(token),
  };
}

export function getAccountStatus(user?: StoredUser | null): StoredUser['account_status'] | null {
  if (!user || typeof user !== 'object') return null;
  const status = user.account_status;
  if (!status || typeof status !== 'object') return null;
  return status;
}

export function isVendorAccessRestricted(user?: StoredUser | null): boolean {
  const status = getAccountStatus(user);
  if (!status) return false;
  if (!status.is_vendor) return false;
  return !Boolean(status.can_access_vendor_portal);
}

export function canVendorSell(user?: StoredUser | null): boolean {
  const status = getAccountStatus(user);
  if (!status || !status.is_vendor) return false;
  return Boolean(status.can_sell);
}

export function canVendorUsePos(user?: StoredUser | null): boolean {
  const status = getAccountStatus(user);
  if (!status || !status.is_vendor) return false;
  return Boolean(status.can_use_pos);
}
